import { generatePath } from 'react-router-dom';
import { AppTableProps } from 'components/organisms';
import { SettlementSummaryV2SimplePageableDto } from 'types';
import { settlementTexts } from 'consts/text';
import { settlementChildrenDTOFields } from 'consts/settlementChildren/settlementChildrenDTOFields';
import { FirstAndLastNameDisplay } from 'components/molecules';
import { appPaths } from 'consts/paths/paths';
import { parseStringIntoFormattedStringDate } from 'utils/parsers/dateTime/parseStringIntoFormattedStringDate';
import { getFormattedPrice } from 'utils/getFormattedPrice';
import { AccountActiveMarkIcon, AccountInactiveMarkIcon } from 'assets';
import { BalanceTag } from 'components/molecules/BalanceTag/BalanceTag';

export const getColumns: () => AppTableProps<SettlementSummaryV2SimplePageableDto>['columns'] =
    () => [
        {
            title: settlementTexts.children.columnLabels.fullName,
            dataIndex: settlementChildrenDTOFields.lastName,
            key: settlementChildrenDTOFields.lastName,
            render: (_, record) => (
                <FirstAndLastNameDisplay
                    firstName={record.childInfoBasic.name}
                    lastName={record.childInfoBasic.lastName}
                    to={generatePath(
                        `${
                            appPaths.app.settlement.children.pickedChild.details.base
                        }?date=${new URLSearchParams(window.location.search).get('date')}`,
                        {
                            childId: record.childInfoBasic.childId,
                        },
                    )}
                />
            ),
        },
        {
            title: settlementTexts.children.columnLabels.group,
            dataIndex: settlementChildrenDTOFields.mainGroup,
            key: settlementChildrenDTOFields.mainGroup,
            align: 'center',
            isVisibleAsExtendableInMobile: true,
            render: (_, record) => record.childInfoBasic.groupName,
        },
        {
            title: settlementTexts.children.columnLabels.endOfContract,
            dataIndex: settlementChildrenDTOFields.settlementPeriodEnd,
            key: settlementChildrenDTOFields.settlementPeriodEnd,
            align: 'center',
            isVisibleAsExtendableInMobile: true,
            render: (_, record) =>
                parseStringIntoFormattedStringDate(
                    record.childInfoBasic.agreementEnd,
                    'YYYY-MM-DD',
                    'DD/MM/YYYY',
                ),
        },
        {
            title: settlementTexts.children.columnLabels.stay,
            dataIndex: settlementChildrenDTOFields.stayGrossCost,
            key: settlementChildrenDTOFields.stayGrossCost,
            align: 'center',
            isVisibleAsExtendableInMobile: true,
            render: (_, record) => (record.computed ? getFormattedPrice(record.stayGrossFee) : '-'),
        },
        {
            title: settlementTexts.children.columnLabels.diet,
            dataIndex: settlementChildrenDTOFields.mealGrossCost,
            key: settlementChildrenDTOFields.mealGrossCost,
            align: 'center',
            isVisibleAsExtendableInMobile: true,
            render: (_, record) => (record.computed ? getFormattedPrice(record.mealGrossFee) : '-'),
        },
        {
            title: settlementTexts.children.columnLabels.additionalLessons,
            dataIndex: settlementChildrenDTOFields.lessonsGrossCost,
            key: settlementChildrenDTOFields.lessonsGrossCost,
            align: 'center',
            isVisibleAsExtendableInMobile: true,
            render: (_, record) =>
                record.computed ? getFormattedPrice(record.lessonsGrossFee) : '-',
        },
        {
            title: settlementTexts.children.columnLabels.summary,
            dataIndex: settlementChildrenDTOFields.totalGrossCost,
            key: settlementChildrenDTOFields.totalGrossCost,
            align: 'center',
            isInvisibleInMobile: true,
            render: (_, record) =>
                record.computed ? getFormattedPrice(record.grossSettlementSummary) : '-',
        },

        {
            title: settlementTexts.children.columnLabels.verified,
            dataIndex: settlementChildrenDTOFields.verified,
            key: settlementChildrenDTOFields.verified,
            align: 'center',
            render: (verified: SettlementSummaryV2SimplePageableDto['verified']) =>
                verified ? <AccountActiveMarkIcon /> : <AccountInactiveMarkIcon />,
        },

        {
            title: settlementTexts.children.columnLabels.additionalCosts,
            dataIndex: settlementChildrenDTOFields.additionalCostsGrossCost,
            key: settlementChildrenDTOFields.additionalCostsGrossCost,
            align: 'center',
            isInvisibleInMobile: true,
            render: (_, record) =>
                record.computed ? getFormattedPrice(record.additionalCostGrossSummary) : '-',
        },
        {
            title: settlementTexts.children.columnLabels.balance,
            dataIndex: settlementChildrenDTOFields.balance,
            key: settlementChildrenDTOFields.balance,
            align: 'center',
            isInvisibleInMobile: true,
            render: (_, record) => <BalanceTag amount={record.childBalance} />,
        },
    ];
